import { useState } from 'react';
import styles from '../GenerationWorkspace.module.css';
import { brandConfig } from '../../../config/brands';
import type { GenerationSettings } from '../state/types';
import { Tooltip } from '../../common/Tooltip';
import { HelpTooltip } from '../../common/HelpTooltip';
import { PromptHistoryModal } from '../../common/PromptHistoryModal';
import { PROMPT_HISTORY_KEYS } from '../../../utils/promptHistory';
import { saveGenerateTextPreference } from '../state/settingsStorage';

/**
 * テキストセクション (テキスト自動生成 / メインコピー / CTA / 注意書き / 追加プロンプト)。
 * GenerationWorkspace の JSX からの切り出しで、DOM 構造・クラス名は不変。
 *
 * 追加プロンプト履歴の state は「属性モード以外に切り替えても保持する」必要があるため
 * 親 (GenerationWorkspace) に残し、ここには props で受け取ったハンドラだけを置く。
 */
export function TextSection({
  settings,
  setSettings,
  setAttribute,
  textSummary,
  hasAnyProductLinks,
  brandDescriptionAvailable,
  brandTaglines,
  showHistory,
  onToggleHistory,
  onCloseHistory,
  onSelectHistory,
  isOpen,
  onToggle,
  onClose,
  layoutApplied = false,
}: {
  settings: GenerationSettings;
  setSettings: (next: GenerationSettings | ((prev: GenerationSettings) => GenerationSettings)) => void;
  setAttribute: (key: string, value: unknown) => void;
  textSummary: string;
  hasAnyProductLinks: boolean;
  brandDescriptionAvailable: boolean;
  brandTaglines: { brandName: string; tagline: string }[];
  showHistory: boolean;
  onToggleHistory: () => void;
  onCloseHistory: () => void;
  onSelectHistory: (prompt: string) => void;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  /** バナーレイアウト適用中 (6欄の値はレイアウトの同じ種類の最初の枠に入る。文言の正本はこの欄・レイアウトは配置だけ) */
  layoutApplied?: boolean;
}) {
  // 詳細設定 (テキスト(商品名・キャンペーン名・特典など) / 価格 / 日付) の開閉。値が入っていれば開いた状態で始める
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(
    () => !!settings.attributes?.display_text || !!settings.attributes?.price_text || !!settings.attributes?.date_text,
  );
  return (
    <div
      className={`accordion-section ${isOpen ? 'accordion-section-open' : ''}`}
      style={{ order: 999999 }}
    >
      <button
        type="button"
        className="accordion-header"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="accordion-title">
          <img src="/pencil_1.svg" alt="" className="accordion-title-icon" />
          <span>テキスト</span>
          <span className="accordion-summary">{textSummary}</span>
        </span>
        <span className={`accordion-icon ${isOpen ? 'accordion-icon-open' : ''}`}>
          {isOpen ? '▼' : '▶'}
        </span>
      </button>
      <div className={`accordion-panel ${isOpen ? 'accordion-panel-open' : 'accordion-panel-closed'}`}>
        <label className="gen-label" style={{ flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            checked={settings.exclude_text_response ?? false}
            onChange={(event) => {
              setSettings((prev) => ({
                ...prev,
                exclude_text_response: event.target.checked,
              }));
            }}
            style={{ width: 'auto', margin: 0 }}
          />
          <span>テキストを含めない（画像のみ生成）</span>
        </label>
        {!settings.exclude_text_response && (
          <>
            {(() => {
              // 3値: 指定なし (false/未設定) / 生成する (true) / 生成しない ('never')
              const rawGenerateText = settings.attributes?.generate_text_from_ec;
              const generateTextMode = rawGenerateText === true ? 'generate' : rawGenerateText === 'never' ? 'never' : 'none';
              // 「生成する」だけは商品/ブランド情報が必要。指定なし/生成しないは常に選択可
              const canGenerateFromInfo = hasAnyProductLinks || brandDescriptionAvailable;
              const generateButton = (
                <button
                  type="button"
                  className={`${styles.optionButton} ${generateTextMode === 'generate' ? styles.optionButtonActive : ''}`}
                  style={canGenerateFromInfo ? undefined : { opacity: 0.3, cursor: 'not-allowed' }}
                  onClick={() => {
                    if (!canGenerateFromInfo) return;
                    setAttribute('generate_text_from_ec', true);
                    saveGenerateTextPreference('generate');
                  }}
                >
                  生成する
                </button>
              );
              return (
                <div className="gen-label">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {hasAnyProductLinks || !brandDescriptionAvailable ? `${brandConfig.labels.productShort}情報` : 'ブランド情報'}からのテキスト生成
                    <HelpTooltip contentKey="generateTextFromProduct" ariaLabel={`${brandConfig.labels.productShort}情報からのテキスト生成のヘルプ`} />
                  </span>
                  <div className={styles.optionGroupButtons}>
                    <button
                      type="button"
                      className={`${styles.optionButton} ${generateTextMode === 'none' ? styles.optionButtonActive : ''}`}
                      onClick={() => {
                        setAttribute('generate_text_from_ec', false);
                        saveGenerateTextPreference('none');
                      }}
                    >
                      指定なし
                    </button>
                    {canGenerateFromInfo ? generateButton : (
                      <Tooltip content={`${brandConfig.labels.productShort}情報を利用するためには素材に${brandConfig.labels.productShort}を紐づけてください`}>
                        {generateButton}
                      </Tooltip>
                    )}
                    <button
                      type="button"
                      className={`${styles.optionButton} ${generateTextMode === 'never' ? styles.optionButtonActive : ''}`}
                      onClick={() => {
                        setAttribute('generate_text_from_ec', 'never');
                        saveGenerateTextPreference('never');
                      }}
                    >
                      生成しない
                    </button>
                  </div>
                </div>
              );
            })()}
            {layoutApplied && (
              <p style={{ margin: 0, fontSize: '12px', color: 'rgba(245, 241, 230, 0.6)' }}>
                レイアウト適用中: 各欄の文言は、レイアウトの同じ種類の枠に入ります。空の枠の文言はAIが考えます。
              </p>
            )}
            <div className="gen-label">
              <div className="prompt-label-row">
                <span>メインコピー</span>
                {brandTaglines.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {brandTaglines.map((bt) => (
                      <button
                        key={bt.tagline}
                        type="button"
                        className="prompt-history-toggle"
                        style={{
                          border: '1px solid rgba(var(--brand-primary-rgb) / 0.5)',
                          borderRadius: '4px',
                          padding: '1px 6px',
                        }}
                        title={`「${bt.tagline}」をメインコピーに挿入`}
                        onClick={() => setAttribute('main_copy', bt.tagline)}
                      >
                        {bt.tagline}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <input
                type="text"
                maxLength={35}
                value={(settings.attributes?.main_copy as string | undefined) ?? ''}
                onChange={(event) => {
                  setAttribute('main_copy', event.target.value);
                }}
                placeholder="最大35文字"
              />
            </div>
            <label className="gen-label">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                CTA
                <HelpTooltip contentKey="cta" ariaLabel="CTAのヘルプ" />
              </span>
              <input
                type="text"
                maxLength={35}
                value={(settings.attributes?.cta as string | undefined) ?? ''}
                onChange={(event) => {
                  setAttribute('cta', event.target.value);
                }}
                placeholder="最大35文字"
              />
            </label>
            <label className="gen-label">
              <span>注意書き</span>
              <input
                type="text"
                value={(settings.attributes?.disclaimer_text as string | undefined) ?? ''}
                onChange={(event) => {
                  setAttribute('disclaimer_text', event.target.value);
                }}
                placeholder="注意書きの内容を入力"
              />
            </label>
          </>
        )}
        <div className="prompt-history-container">
          <div className="prompt-label-row">
            <span className="gen-label-text">追加プロンプト</span>
            <button
              type="button"
              className="prompt-history-toggle"
              onClick={onToggleHistory}
            >
              <span aria-hidden="true" style={{ display: 'inline-block', width: 12, height: 12, marginRight: 4, verticalAlign: '-2px', backgroundColor: 'currentColor', WebkitMaskImage: 'url(/time_clock_circle.svg)', maskImage: 'url(/time_clock_circle.svg)', WebkitMaskSize: 'contain', maskSize: 'contain', WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat', WebkitMaskPosition: 'center', maskPosition: 'center' }} />
              履歴
            </button>
          </div>
          <textarea
            value={(settings.attributes?.other_instructions as string | undefined) ?? ''}
            onChange={(event) => {
              setAttribute('other_instructions', event.target.value);
            }}
            placeholder="自由入力"
            rows={1}
            className="gen-textarea"
          />
          <PromptHistoryModal
            isOpen={showHistory}
            onClose={onCloseHistory}
            scope="image_additional"
            storageKey={PROMPT_HISTORY_KEYS.IMAGE_GENERATION_ATTRIBUTES}
            onSelect={onSelectHistory}
            title="追加プロンプトの履歴"
          />
        </div>
        {/* 詳細設定 (テキスト(商品名・キャンペーン名・特典など) / 価格 / 日付) は追加プロンプトの下 */}
        {!settings.exclude_text_response && (
          <div className={styles.compositionAdvancedSection}>
            <button
              type="button"
              onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
              aria-expanded={isAdvancedOpen}
              className={styles.compositionAdvancedToggle}
            >
              <svg
                width="8"
                height="8"
                viewBox="0 0 8 8"
                fill="currentColor"
                style={{ transition: 'transform 0.2s', transform: isAdvancedOpen ? 'rotate(90deg)' : 'none' }}
              >
                <path d="M2 0l4 4-4 4z" />
              </svg>
              <span>詳細設定</span>
            </button>
            {isAdvancedOpen && (
              <>
                <label className="gen-label">
                  <span>テキスト（商品名・キャンペーン名・特典など）</span>
                  <textarea
                    className="gen-textarea"
                    rows={2}
                    value={(settings.attributes?.display_text as string | undefined) ?? ''}
                    onChange={(event) => {
                      setAttribute('display_text', event.target.value);
                    }}
                    placeholder="画像にそのまま入れる文字"
                      />
                </label>
                <label className="gen-label">
                  <span>価格</span>
                  <input
                    type="text"
                    value={(settings.attributes?.price_text as string | undefined) ?? ''}
                    onChange={(event) => {
                      setAttribute('price_text', event.target.value);
                    }}
                    placeholder="例: 1,980円(税込)"
                  />
                </label>
                <label className="gen-label">
                  <span>日付</span>
                  <input
                    type="text"
                    value={(settings.attributes?.date_text as string | undefined) ?? ''}
                    onChange={(event) => {
                      setAttribute('date_text', event.target.value);
                    }}
                    placeholder="例: 10/20(月)〜10/31(金)"
                      />
                </label>
              </>
            )}
          </div>
        )}
        <div className={styles.accordionPanelFooter}>
          <Tooltip content="閉じる">
            <button
              type="button"
              className={styles.iconButton}
              onClick={onClose}
            >
              <img src="/move_collapse_vertical.svg" alt="閉じる" style={{ width: '16px', height: '16px' }} />
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
